import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Toaster } from 'react-hot-toast';
import { Box, useMediaQuery } from '@mui/material'; // Moved useMediaQuery here
import Dashboard from './components/Dashboard';
import Login from './components/Login';
import VerifyCode from './components/VerifyCode';
import APIReference from './components/APIReference';
import Contacts from './components/Contacts';
import Chats from './components/Chats';
import Messages from './components/Messages';
import TokenManager from './components/TokenManager';
import Settings from './components/Settings';
import Sidebar from './components/Sidebar';
import Navbar from './components/Navbar';
import config from './config';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#0088cc',
    },
    secondary: {
      main: '#2b2d42',
    },
    background: {
      default: '#0f172a',
      paper: '#1e293b',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiDrawer: {
      styleOverrides: {
        paper: {
          '@media (max-width: 600px)': {
            width: '100%',
            maxWidth: '320px',
          },
        },
      },
    },
  },
});

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('telegram_token'));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  // Check for mobile view - useMediaQuery is imported from @mui/material
  const isMobile = useMediaQuery(darkTheme.breakpoints.down('sm'));

  // Auto-close sidebar on mobile when route changes
  const handleRouteChange = () => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  // Initialize sidebar state based on screen size
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    } else {
      setSidebarOpen(true);
    }
  }, [isMobile]);

  useEffect(() => {
    if (token) {
      setIsLoggedIn(true);
    }
  }, [token]);

  const handleLogin = (newToken) => {
    localStorage.setItem('telegram_token', newToken);
    setToken(newToken);
    setIsLoggedIn(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('telegram_token');
    localStorage.removeItem('phone_number');
    localStorage.removeItem('phone_code_hash');
    setToken(null);
    setIsLoggedIn(false);
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  if (!isLoggedIn) {
    return (
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        <Toaster position="top-right" />
        <Router basename={config.WEB_BASE}>
          <Routes>
            <Route path="/login" element={<Login onLogin={handleLogin} />} />
            <Route path="/verify" element={<VerifyCode onLogin={handleLogin} />} />
            <Route path="*" element={<Navigate to="/login" />} />
          </Routes>
        </Router>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Toaster 
        position={isMobile ? "top-center" : "top-right"} 
        toastOptions={{
          duration: 4000,
          style: {
            background: '#1e293b',
            color: '#fff',
            borderRadius: '12px',
            padding: isMobile ? '12px 16px' : '16px 24px',
            fontSize: isMobile ? '0.875rem' : '1rem',
          },
        }}
      />
      <Router basename={config.WEB_BASE}>
        <Box sx={{ 
          display: 'flex', 
          minHeight: '100vh', 
          bgcolor: 'background.default',
          flexDirection: { xs: 'column', sm: 'row' }
        }}>
          <Sidebar 
            open={sidebarOpen} 
            onClose={() => setSidebarOpen(false)} 
            onLogout={() => {
              handleLogout();
              handleRouteChange();
            }}
          />
          
          {/* Main content area */}
          <Box sx={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            ml: { 
              xs: 0, 
              sm: sidebarOpen ? '280px' : '0' 
            },
            transition: darkTheme.transitions.create(['margin'], {
              easing: darkTheme.transitions.easing.sharp,
              duration: darkTheme.transitions.duration.leavingScreen,
            }),
            width: '100%',
            overflow: 'hidden'
          }}>
            <Navbar onMenuClick={toggleSidebar} />
            
            {/* Main content container */}
            <Box 
              component="main" 
              sx={{ 
                flex: 1,
                p: { xs: 2, sm: 3 },
                overflow: 'auto',
                mt: { xs: '56px', sm: '64px' }, // Adjust for mobile/desktop navbar height
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}
            >
              <Routes>
                <Route path="/" element={<Dashboard token={token} />} />
                <Route path="/contacts" element={<Contacts token={token} />} />
                <Route path="/chats" element={<Chats token={token} />} />
                <Route path="/messages" element={<Messages token={token} />} />
                <Route path="/token-manager" element={<TokenManager />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/api-reference" element={<APIReference />} />
                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            </Box>
          </Box>
        </Box>
      </Router>
    </ThemeProvider>
  );
}

export default App;